import React, { useState } from 'react'
import { Button } from '../components/ui'
import { EmptyShop } from '../components/EmptyShop'
import { useProducts, useProductOptions, groupOptionsByType } from '../hooks/useProducts'
import { Database } from '../lib/supabase'
import './Shop.css'

type Product = Database['public']['Tables']['products']['Row']

export const Shop: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [sortBy, setSortBy] = useState<string>('name')
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({})

  const { products, loading, error } = useProducts()
  const { options: productOptions } = useProductOptions(selectedProduct?.id)
  const { options: allOptions } = useProductOptions() // Get all options to check which products have them

  // Main categories
  const mainCategories = [
    { id: 'All', name: 'All', description: 'Browse all products' },
    { id: 'Slides', name: 'Slides', description: 'Custom slides and frames' },
    { id: 'Accessories', name: 'Accessories', description: 'Platform accessories' },
    { id: 'Swag', name: 'Swag', description: 'Apparel and merchandise' }
  ]

  const subCategories = Array.from(new Set(products.map(p => p.category)))

  const filteredProducts = products
    .filter(product => selectedCategory === 'All' || product.category === selectedCategory)
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.base_price - b.base_price
        case 'price-high':
          return b.base_price - a.base_price
        case 'name':
        default:
          return a.name.localeCompare(b.name)
      }
    })

  const groupedOptions = groupOptionsByType(productOptions)

  // Helper function to check if a product has options
  const productHasOptions = (productId: string) => {
    return allOptions.some(option => option.product_id === productId)
  }

  const calculateTotalPrice = (product: Product, options: Record<string, string>) => {
    let total = product.base_price
    Object.values(options).forEach(optionName => {
      const option = productOptions.find(opt => opt.option_name === optionName)
      if (option) {
        total += option.price_modifier
      }
    })
    return total
  }

  const handleOptionChange = (optionType: string, optionName: string) => {
    setSelectedOptions(prev => {
      // If the same option is clicked, de-select it
      if (prev[optionType] === optionName) {
        const newOptions = { ...prev }
        delete newOptions[optionType]
        return newOptions
      }
      // Otherwise, select the new option
      return {
        ...prev,
        [optionType]: optionName
      }
    })
  }

  const resetOptions = () => {
    setSelectedOptions({})
    setSelectedProduct(null)
  }

  if (loading) {
    return (
      <div className="shop">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading products...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="shop">
        <div className="error-state">
          <p>Error loading products: {error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    )
  }

  // Show empty state when no active products
  if (!loading && products.length === 0) {
    return (
      <div className="shop">
        <EmptyShop />
      </div>
    )
  }

  return (
    <div className="shop">
      <div className="shop-header">
        <div className="main-categories">
          {mainCategories.map(category => (
            <button
              key={category.id}
              className={`main-category ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              <span className="category-name">{category.name}</span>
              <span className="category-description">{category.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="shop-content">
        <div className="shop-sidebar">
          {selectedCategory !== 'All' && (
            <div className="filter-section">
              <h3>Subcategories</h3>
              <div className="subcategory-filters">
                {subCategories
                  .filter(sub => {
                    if (selectedCategory === 'Slides') return sub.includes('Slide')
                    if (selectedCategory === 'Accessories') return sub.includes('Accessory') || sub.includes('Magwell') || sub.includes('Baseplate')
                    if (selectedCategory === 'Swag') return sub.includes('Shirt') || sub.includes('Hoodie') || sub.includes('Sticker') || sub.includes('Hat') || sub.includes('Flag')
                    return true
                  })
                  .map(category => (
                    <button
                      key={category}
                      className={`subcategory-filter ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category}
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="filter-section">
            <h3>Sort By</h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="name">Name A-Z</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>

        <div className="shop-main">
          {selectedProduct ? (
            <div className="product-detail">
              <div className="product-detail-header">
                <h2>{selectedProduct.name}</h2>
                <Button variant="ghost" onClick={resetOptions}>
                  ← Back to Products
                </Button>
              </div>

              <div className="product-detail-content">
                <div className="product-image-large">
                  <div className="image-placeholder">
                    <span>{selectedProduct.name}</span>
                  </div>
                </div>

                <div className="product-detail-info">
                  <div className="product-detail-meta">
                    <p className="product-code">Product Code: {selectedProduct.product_code}</p>
                    <p className="product-category">{selectedProduct.category}</p>
                    <p className="product-description">{selectedProduct.description}</p>
                  </div>

                  <div className="product-options">
                    {Object.entries(groupedOptions).map(([optionType, options]) => (
                      <div key={optionType} className="option-group">
                        <h4>{optionType.replace('_', ' ').toUpperCase()}</h4>
                        <div className="option-buttons">
                          {options.map(option => (
                            <button
                              key={option.id}
                              className={`option-button ${selectedOptions[optionType] === option.option_name ? 'active' : ''}`}
                              onClick={() => handleOptionChange(optionType, option.option_name)}
                            >
                              {option.option_name}
                              {option.price_modifier > 0 && (
                                <span className="option-price">+${option.price_modifier}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="product-detail-footer">
                    <div className="price-display">
                      <span className="base-price">Base: ${selectedProduct.base_price.toFixed(2)}</span>
                      <span className="total-price">
                        Total: ${calculateTotalPrice(selectedProduct, selectedOptions).toFixed(2)}
                      </span>
                    </div>
                    <Button variant="primary" size="md">
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <div className="image-placeholder">
                      <span>{product.name}</span>
                    </div>
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-code">{product.product_code}</p>
                    <p className="product-category">{product.category}</p>
                    <p className="product-description">{product.description}</p>
                    <div className="product-footer">
                      <span className="product-price">${product.base_price.toFixed(2)}</span>
                      {product.category === 'Swag' || !productHasOptions(product.id) ? (
                        <Button
                          variant="primary"
                          size="sm"
                        >
                          Add to Cart
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setSelectedProduct(product)}
                        >
                          Customize
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
